import { cn } from '@/lib/utils';
import RewardCoin from '@/components/Icons/RewardCoin';
import { Tier, TIER_HEX, TIER_THRESHOLDS } from '@/lib/points';

const TIERS: { tier: Tier; floor: number }[] = [
  { tier: 'bronze', floor: 0 },
  { tier: 'silver', floor: TIER_THRESHOLDS.silver },
  { tier: 'gold', floor: TIER_THRESHOLDS.gold },
  { tier: 'platinum', floor: TIER_THRESHOLDS.platinum },
];

const fillOf = (points: number, from: number, to: number) =>
  Math.min(1, Math.max(0, (points - from) / (to - from)));

export const tierLabel = (tier: Tier) =>
  tier.charAt(0).toUpperCase() + tier.slice(1);

const indexOf = (tier: Tier) => TIERS.findIndex((row) => row.tier === tier);

/** Counted from the achieved tier, so spending never shows a lower goal. */
export const nextTierText = (tier: Tier, points: number) => {
  const next = TIERS[indexOf(tier) + 1];
  if (!next) return 'Top tier reached';
  const needed = Math.max(0, next.floor - points);
  return `${needed.toLocaleString()} pts to ${tierLabel(next.tier)}`;
};

/** Segments up to the achieved tier stay full; only the next one tracks the balance. */
const segmentFill = (i: number, achieved: number, points: number) => {
  if (i <= achieved) return 1;
  if (i === achieved + 1)
    return fillOf(points, TIERS[i - 1].floor, TIERS[i].floor);
  return 0;
};

function Connector({ fill }: { fill: number }) {
  return (
    <span className="absolute right-1/2 top-6 h-1.5 w-full -translate-y-1/2 overflow-hidden rounded-full bg-green-200/60">
      <span
        className="block h-full w-full origin-left bg-green-600 transition-transform duration-500 ease-out-strong"
        style={{ transform: `scaleX(${fill})` }}
      />
    </span>
  );
}

const glowOf = (tier: Tier) => ({
  boxShadow: `0 0 0 2px ${TIER_HEX[tier]}, 0 4px 14px ${TIER_HEX[tier]}66`,
});

function StopCoin({
  tier,
  reached,
  isCurrent,
}: {
  tier: Tier;
  reached: boolean;
  isCurrent: boolean;
}) {
  const size = isCurrent ? 40 : 28;
  return (
    <span className="relative z-10 flex h-12 items-center justify-center">
      <span
        className="rounded-full bg-green-50 p-[3px]"
        style={isCurrent ? glowOf(tier) : undefined}
      >
        <RewardCoin
          coinType={tier}
          width={size}
          height={size}
          className={cn(!reached && 'opacity-40 grayscale')}
        />
      </span>
    </span>
  );
}

function TierStop({
  index,
  achieved,
  points,
}: {
  index: number;
  achieved: number;
  points: number;
}) {
  const { tier, floor } = TIERS[index];
  const isCurrent = index === achieved;
  return (
    <li
      className="relative flex flex-1 flex-col items-center"
      aria-current={isCurrent ? 'step' : undefined}
    >
      {index > 0 && <Connector fill={segmentFill(index, achieved, points)} />}
      <StopCoin tier={tier} reached={index <= achieved} isCurrent={isCurrent} />
      <span
        className={cn(
          'mt-1 text-xs',
          isCurrent ? 'text-sm font-bold text-green-950' : 'text-green-900/60'
        )}
      >
        {tierLabel(tier)}
      </span>
      <span className="text-[11px] tabular-nums text-green-900/50">
        {floor.toLocaleString()} pts
      </span>
    </li>
  );
}

/** The four tier coins as stops on one line; spending never moves you back. */
export default function TierRoad({
  points,
  tier,
}: {
  points: number;
  tier: Tier;
}) {
  const achieved = indexOf(tier);
  return (
    <ol
      className="flex"
      aria-label={`${tierLabel(tier)} tier, ${points.toLocaleString()} points`}
    >
      {TIERS.map((row, index) => (
        <TierStop
          key={row.tier}
          index={index}
          achieved={achieved}
          points={points}
        />
      ))}
    </ol>
  );
}
